/**
 * Copyright (c) 2023, CYBRA Corp. All rights reserved.
 */
import { Component, OnInit, Input } from "@angular/core";
import { TypeaheadMatch } from 'ngx-bootstrap/typeahead';
import { Observable, Observer } from 'rxjs';
import { switchMap } from 'rxjs/operators';

import { Rule } from "../../../models/rule";
import { RuleCondition, Operator, OperatorUtil, Type } from "../../../models/rule-condition";
import { EmGroup } from "../../../models/em-group";
import { GroupType } from '../../../models/group-type';
import { Property } from '../../../models/property';
import { PropertyType } from '../../../models/property-type';
import { LocationInstance } from '../../../models/location-instance';
import { ObjectInstance } from '../../../models/object-instance';
import { TagInstance } from '../../../models/tag-instance';
import { ZoneInstance } from '../../../models/zone-instance';
import { User } from '../../../models/user';

import { EntityService } from "../../../services/entity.service";
import { GroupService } from "../../../services/group.service";
import { LocationService } from "../../../services/location.service";
import { ObjectService } from "../../../services/object.service";
import { TagService } from "../../../services/tag.service";
import { UserService } from "../../../services/user.service";
import { ZoneService } from "../../../services/zone.service";
import { ObjectDefinition } from "app/models/object-definition";
import { BaseEntity } from "app/models/base-entity";
import { ReaderService } from "app/services/reader.service";
import { AntennaService } from "app/services/antenna.service";
import { DriverObjectService } from "app/services/driver-object.service";
import { ServiceService } from "app/services/service.service";
import { ServerObjectService } from "app/services/server-object.service";
import { ReaderInstance } from "app/models/reader-instance";

@Component({
  selector: 'app-entity-parameter-editor',
  templateUrl: './entity-parameter-editor.component.html'
})
export class EntityParameterEditorComponent implements OnInit {

    @Input()
    rule: Rule;
    
    @Input()
    ruleCondition: RuleCondition;
    
    //Holds the available Operators for the Condition
    entityOperators: Operator[] = [];
    
    //Holds whether the "Body" of the Condition should be / is displayed to the User
    showBody = false;
    
    //Variables for "Type-Ahead" logic when setting values
    typeaheadValue: string;
    typeaheadLoading: boolean;
    typeaheadNoResults: boolean;
    entities: Observable<any>;

    //Variables for the "Has Value of Property" Operator logic
    mPropertyOptions: PropertyOption[] = [];
    mPropertyOperators = [Operator.EQUAL, Operator.NOT_EQUAL];

    constructor(
        private entityService: EntityService,
        private locationService: LocationService,
        private objectService: ObjectService,
        private tagService: TagService,
        private userService: UserService,
        private zoneService: ZoneService,
        private readerService: ReaderService,
        private antennaService: AntennaService,
        private driverObjectService: DriverObjectService,
        private serviceService: ServiceService,
        private serverObjectService: ServerObjectService
    ) { }


    ngOnInit(): void {
        this.entityOperators = OperatorUtil.getOperatorsForPropertyType(this.ruleCondition == null? null : this.ruleCondition.propertyType);
        
        this.showBody = this.ruleCondition != null && this.ruleCondition.id != null ? false : true;
        
        //KHT 08/17/2021 - updated to new ngx-bootstrap standard
        this.entities = new Observable((observer: Observer<string>) => {
            observer.next(this.typeaheadValue); 
        }).pipe(
            switchMap((query: string) => {
                if(query){
                    return this.search(query);
                }
            })
        )

        this.loadReferenceValue();
        this.refreshPropertyOptions();
    }

    getTitle(): string {
        let title = this.getParameterLabel() + " ";
        if (this.ruleCondition.operator == Operator.HAS_PROPERTY_WITH_VALUE) {
            title = title + 
                "has Data for Property: " + 
                    (this.selectedPropertyOption == null? "*NULL*" : ("'" + this.selectedPropertyOption.mPropertyName + "' (" + this.selectedPropertyOption.mObjectName + ")")) + " " + 
                "where the Value " + 
                    (this.ruleCondition.propertyOperator == null? "*NULL*" : this.getOperatorLabel(this.ruleCondition.propertyOperator)) + " " +  
                    (this.ruleCondition.value == null? "*NULL*" : ("'" + this.ruleCondition.value + "'"));
        } else {
            title = title + this.getOperatorLabel(this.ruleCondition.operator) + " " + this.typeaheadValue;
        }
        return title;
    }

    getParameterLabel(): string {
        if (this.rule != null && this.rule.event != null && this.rule.event.properties != null) {
            const property = this.rule.event.properties.find(p => p.property == this.ruleCondition.property);
            return property != null ? property.name : "?";
        } else {
            return "?";
        }
    }
    
    getOperatorLabel(operator: Operator): string {
        return OperatorUtil.getOperatorLabel(operator);
    }

    async loadReferenceValue(): Promise<void> {
        console.log("loadReferenceValue:-" + JSON.stringify(this.ruleCondition));
        if (this.ruleCondition.propertyValue != null || this.ruleCondition.value != null) {
            switch (this.ruleCondition.propertyType) {
                case PropertyType.OBJECT_REFERENCE:
                    const object: ObjectInstance = await this.objectService.getObjectInstanceForId(Number(this.ruleCondition.propertyValue));
                    if (object != null) {
                        this.typeaheadValue = object.name;
                    }
                    break;
                case PropertyType.LOCATION_REFERENCE:
                    const location: LocationInstance = await this.locationService.getLocationInstanceById(Number(this.ruleCondition.propertyValue));
                    if (location != null) {
                        this.typeaheadValue = location.name;
                    }
                    break;
                case PropertyType.TAG_REFERENCE:
                    const tag: TagInstance = await this.tagService.getTagInstanceById(Number(this.ruleCondition.propertyValue));
                    if (tag != null) {
                        this.typeaheadValue = tag.name;
                    }
                    break;
                case PropertyType.USER_REFERENCE:
                    const user: User = await this.userService.getUserForId(Number(this.ruleCondition.propertyValue));
                    if (user != null) {
                        this.typeaheadValue = user.firstName + " " + user.lastName;
                    }
                    break;
                case PropertyType.ZONE_REFERENCE:
                    const zone: ZoneInstance = await this.zoneService.getZoneInstanceById(Number(this.ruleCondition.propertyValue));
                    if (zone != null) {
                        this.typeaheadValue = zone.name;
                    }
                    break;
                case PropertyType.READER_REFERENCE:
                    const reader: ReaderInstance = await this.readerService.getReaderInstanceById(Number(this.ruleCondition.value));
                    if (reader != null) {
                        this.typeaheadValue = reader.name;
                    }
                    break;
            }
        }
    }
    
    changeTypeaheadLoading(e: boolean): void {
        this.typeaheadLoading = e;
    }
    
    typeaheadOnSelect(e: TypeaheadMatch): void {
        console.log('Selected value: ', e.item);
        if (e.item.type == Type.READER) {
            this.ruleCondition.value = e.item.id;
        } else { 
            this.ruleCondition.propertyValue = e.item.id;
        }
    }
    
    search(token: string): Observable<any[]> {
        let propertyType: PropertyType;
        if (this.ruleCondition.propertyType == null) {
            if (this.ruleCondition.sourceType == Type.TAG) {
                propertyType = PropertyType.TAG_REFERENCE;
            } else if (this.ruleCondition.sourceType == Type.OBJECT) {
                propertyType = PropertyType.OBJECT_REFERENCE;
            } else if (this.ruleCondition.sourceType == Type.USER) {
                propertyType = PropertyType.USER_REFERENCE;
            } else if (this.ruleCondition.sourceType == Type.READER) {
                propertyType = PropertyType.READER_REFERENCE;
            }
        } else {
            propertyType = this.ruleCondition.propertyType;
        }
        
        if (propertyType == PropertyType.USER_REFERENCE) {
            return this.entityService.searchUser(token);
        } else {
            return this.entityService.search(propertyType, token);
        }
    }

    remove(): void {
        const index = this.rule.conditions.indexOf(this.ruleCondition);
        if (index > -1) {
            this.rule.conditions.splice(index, 1);
        }
    }

    isConditionOperator_HasPropertyWithValue(): boolean {
        return this.ruleCondition != null && this.ruleCondition.operator == Operator.HAS_PROPERTY_WITH_VALUE;
    }
    
    private async refreshPropertyOptions(): Promise<void> {
        const options: PropertyOption[] = await this.getPropertyOptions();
        this.mPropertyOptions = options == null? [] : options;
    }

    private async getPropertyOptions(): Promise<PropertyOption[]> {
        if (this.ruleCondition != null && this.ruleCondition.propertyType != null) {
            switch (this.ruleCondition.propertyType) {
                case PropertyType.OBJECT_REFERENCE: {
                    return await this.getPropertyOptions_Objects();
                }
                case PropertyType.TAG_REFERENCE: {
                    return await this.getPropertyOptions_Tags();
                }
                case PropertyType.LOCATION_REFERENCE: {
                    return await this.getPropertyOptions_Locations();
                }
                case PropertyType.ZONE_REFERENCE: {
                    return await this.getPropertyOptions_Zones();
                }
                case PropertyType.READER_REFERENCE: {
                    return await this.getPropertyOptions_Readers();
                }
                case PropertyType.ANTENNA_REFERENCE: {
                    return await this.getPropertyOptions_Antennas();
                }
                case PropertyType.DRIVEROBJECT_REFERENCE: {
                    return await this.getPropertyOptions_DriverObjects();
                }
                case PropertyType.SERVICE_REFERENCE: {
                    return await this.getPropertyOptions_Services();
                }
            }
        }
        return null;
    }

    private async getPropertyOptions_ServerObjects(): Promise<PropertyOption[]> {
        const rv: PropertyOption[] = [];
        const serverObjects = await this.serverObjectService.getAllServerObjects();
        if (serverObjects != null) {
            for (const serverObject of serverObjects) {
                rv.push(...this.createPropertyOptions(await this.serverObjectService.getServerObjectById(serverObject.id)))
            }
        }
        return rv;
    }

    private async getPropertyOptions_Services(): Promise<PropertyOption[]> {
        const rv: PropertyOption[] = [];
        const services = await this.serviceService.getAllServices();
        if (services != null) {
            for (const service of services) {
                rv.push(...this.createPropertyOptions(await this.serviceService.getServiceById(service.id)))
            }
        }
        return rv;
    }

    private async getPropertyOptions_DriverObjects(): Promise<PropertyOption[]> {
        const rv: PropertyOption[] = [];
        const driverObjects = await this.driverObjectService.getAllDriverObjects();
        if (driverObjects != null) {
            for (const driverObject of driverObjects) {
                rv.push(...this.createPropertyOptions(driverObject));
            }
        }
        return rv;
    }

    private async getPropertyOptions_Readers(): Promise<PropertyOption[]> {
        const rv: PropertyOption[] = [];
        const readers = await this.readerService.getAllReaders();
        if (readers != null) {
            for (const reader of readers) {
                rv.push(...this.createPropertyOptions(await this.readerService.getReaderById(reader.id)));
            }
        }
        return rv;
    }

    private async getPropertyOptions_Antennas(): Promise<PropertyOption[]> {
        const rv: PropertyOption[] = [];
        const antennas = await this.antennaService.getAllAntennas();
        if (antennas != null) {
            for (const antenna of antennas) {
                rv.push(...this.createPropertyOptions(await this.antennaService.getAntennaById(antenna.id)));
            }
        }
        return rv;
    }

    private async getPropertyOptions_Locations(): Promise<PropertyOption[]> {
        const rv: PropertyOption[] = [];
        const locations = await this.locationService.getAllLocations();
        if (locations != null) {
            for (const location of locations) {
                rv.push(...this.createPropertyOptions(await this.locationService.getLocationById(location.id)));
            }
        }
        return rv;
    }

    private async getPropertyOptions_Zones(): Promise<PropertyOption[]> {
        const rv: PropertyOption[] = [];
        const zones = await this.zoneService.getAllZones();
        if (zones != null) {
            for (const zone of zones) {
                rv.push(...this.createPropertyOptions(await this.zoneService.getZoneById(zone.id)));
            }
        }
        return rv;
    }

    private async getPropertyOptions_Tags(): Promise<PropertyOption[]> {
        const rv: PropertyOption[] = [];
        const tags = await this.tagService.getAllTagDefinitions();
        if (tags != null) {
            for (const tag of tags) {
                rv.push(...this.createPropertyOptions(await this.tagService.getTagDefinitionById(tag.id)));
            }
        }
        return rv;
    }

    private async getPropertyOptions_Objects(): Promise<PropertyOption[]> {
        const rv: PropertyOption[] = [];
        {   //Asset Properties
            const assets = await this.objectService.getAllAssetObjects();
            if (assets != null) {
                for (const asset of assets) {
                    rv.push(...this.createPropertyOptions(await this.objectService.getObjectForId(asset.id)));
                }
            }
        }
        {   //People Properties
            const people = await this.objectService.getAllPeopleObjects();
            if (people != null) {
                for (const person of people) {
                    rv.push(...this.createPropertyOptions(await this.objectService.getObjectForId(person.id)));
                }
            }
        }
        return rv;
    }

    private createPropertyOptions(baseEntity: BaseEntity): PropertyOption[] {
        const rv: PropertyOption[] = [];
        if (baseEntity != null && baseEntity.properties != null) {
            for (const property of baseEntity.properties) {
                const option = new PropertyOption();
                option.mValue = property.id.toString();
                option.mPropertyName = property.name;
                option.mObjectName = baseEntity.name;
                rv.push(option);
            }
        }
        return rv;
    }

    get selectedPropertyOption(): PropertyOption {
        if (this.ruleCondition != null && 
            this.ruleCondition.operator != null &&
            this.ruleCondition.operator == Operator.HAS_PROPERTY_WITH_VALUE &&
            this.ruleCondition.propertyValue != null && 
            this.ruleCondition.propertyValue.length > 0
        ) {
            return this.mPropertyOptions.find(option => option.mValue == this.ruleCondition.propertyValue);
        } else {
            return null;
        }
    }

    set selectedPropertyOption(property: PropertyOption) {
        console.log("Setting selected Property Option: " + (property == null? "*NULL*" : property.mPropertyName));
        if (this.ruleCondition != null &&
            this.ruleCondition.operator != null &&
            this.ruleCondition.operator == Operator.HAS_PROPERTY_WITH_VALUE
        ) {
            console.log("Rule Condition's Property Value updated to selected Property Option!");
            this.typeaheadValue = "";
            this.ruleCondition.propertyValue = property.mValue;
        }
    } 

    getLabelForPropertyOption(option: PropertyOption): string {
        if (option == null || option.mValue == null) {
            return "*NULL PROPERTY*";
        } else {
            return option.mPropertyName + " (" + option.mObjectName + ")";
        }
    }

}

class PropertyOption {
    mValue: string;
    mPropertyName: string;
    mObjectName: string;
}
